import React, { useMemo } from 'react';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import type {
	Command as CommandExtension,
	FullPage,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';

import { COMMAND_PALETTE_SECTIONS } from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { CommandActionType } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';

import {
	useProjectContext,
	type ProjectContext,
} from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useRouterActions, type RouterActionsType } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { FORGE_COMMAND_PALETTE_ICON_MAP } from '../../constants.tsx';
import type { CommandsWithAction } from '../../types.tsx';
import { RegisterForgeCommands } from '../../controller/RegisterForgeCommands.tsx';
import { findMatchingModule, navigateToPage } from './util.tsx';

type ForgeLinkCommandsProps = {
	commands: CommandExtension[];
	pageModules: FullPage[];
};
export const ForgeLinkCommands = ({ commands, pageModules }: ForgeLinkCommandsProps) => {
	const { push } = useRouterActions();

	const { data: projectContextData } = useProjectContext();

	const commandsWithActions = useMemo(
		() =>
			generateCommandActions({
				commands,
				projectContextData,
				push,
				pageModules,
			}),
		[commands, pageModules, projectContextData, push],
	);

	return <RegisterForgeCommands commands={commandsWithActions} />;
};

export const generateCommandActions = ({
	commands,
	projectContextData,
	push,
	pageModules,
}: ForgeLinkCommandsProps & {
	push: RouterActionsType['push'];
	projectContextData: ProjectContext | null;
}): Array<CommandsWithAction> => {
	return commands.map((extension) => {
		const { id: rawCommandPaletteExtensionId, properties } = extension;
		const { title, keywords, shortcut, icon, target } = properties;

		const LeftIcon = FORGE_COMMAND_PALETTE_ICON_MAP[icon];
		const components = LeftIcon
			? { LeftIcon }
			: {
					LeftIcon: () => (
						<ArrowRightIcon
							label=""
							aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
						/>
					),
				};

		return {
			id: rawCommandPaletteExtensionId,
			title,
			section: COMMAND_PALETTE_SECTIONS.forge,
			icon,
			keywords: keywords?.join(' '),
			shortcut,
			name: title,
			components,
			primaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => {
					const matchingPageModule = findMatchingModule({
						pageModules,
						rawCommandPaletteExtensionId,
						target,
					});

					if (!matchingPageModule) return null;

					navigateToPage({
						pageModule: matchingPageModule,
						push,
						projectContext: projectContextData,
					});
				},
			},
		};
	});
};
